import React, { useState, useEffect, useRef } from "react"
import * as XLSX from "xlsx"
import {
  Table,
  Input,
  Button,
  Row,
  Col,
  Space,
  Select,
  Card,
  Typography,
  Tag,
  Badge,
  Avatar,
  Switch,
  Tooltip,
  Popover,
} from "antd"
import {
  UserOutlined,
  IdcardOutlined,
  PhoneOutlined,
  BankOutlined,
  EnvironmentOutlined,
  DownloadOutlined,
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  TeamOutlined,
  DownOutlined,
  RightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons"
import { columns } from "./columns"
import { DocumentFilesDialog } from "./document-files"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"

const { Text } = Typography
import {
  upsertPackageInstaller,
  getListPackageInstaller,
  resetPackageInstallerFlag,
} from "store/packageInstaller/actions"
import {
  inactivePackageInstaller,
  filterPackageInstaller,
  filterLoadingPackageInstaller,
  addUserFile,
  deleteUserFile,
  getUserFiles,
  upsertExecutor,
} from "store/actions"
import {
  enumToArrayAnt,
  enumToArrayAntWithTranslate,
  enumToArrayWithTranslate,
} from "helpers/service_helper"
import { PackageCompanyType } from "models/types/package-company-type"
import InstallerModal from "./installer-modal"

const InstallerList = props => {
  const [editingRecord, setEditingRecord] = useState(null)
  const [data, setData] = useState([])
  const [activeKey, setActiveKey] = useState([])
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Filter states
  const [filterFullName, setFilterFullName] = useState("")
  const [filterPackageCompanyType, setFilterPackageCompanyType] =
    useState(undefined)
  const [filterCompanyName, setFilterCompanyName] = useState("")

  const [packageCompanyType, setPackageCompanyType] = useState("")

  useEffect(() => {
    props.onGetListPackageInstaller()

    setPackageCompanyType(
      enumToArrayAntWithTranslate(PackageCompanyType, props.translate, "enums")
    )
  }, [])

  useEffect(() => {
    if (props.lstPackageInstaller && Array.isArray(props.lstPackageInstaller)) {
      setData(
        props.lstPackageInstaller.map(item => ({
          ...item,
          key: item.id,
        }))
      )
    }
  }, [props.lstPackageInstaller])

  useEffect(() => {
    if (props.success)
      props.openNotification &&
        props.openNotification("success", "top", props.success)
    if (props.error)
      props.openNotification &&
        props.openNotification("error", "top", props.error)
    props.onResetPackageInstallerFlag()
  }, [props.success, props.error])

  const handleEdit = record => {
    setEditingRecord(record)
    setIsModalOpen(true)
  }

  // Open modal for creating new installer
  const handleOpenCreateModal = () => {
    setEditingRecord(null)
    setIsModalOpen(true)
  }

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingRecord(null)
  }

  const handleSubmit = updatedData => {
    props.onUpsertPackageInstaller(updatedData)
  }

  const handleToggleActive = (checked, record) => {
    const updatedData = {
      ...record,
      isActive: Boolean(checked),
    }
    props.onUpsertPackageInstaller(updatedData)
  }

  const handleIsLimitCity = (checked, record) => {
    const updatedData = {
      ...record,
      isLimitCity: Boolean(checked),
    }
    props.onUpsertPackageInstaller(updatedData)
  }

  // Table change to match executor table
  const onChange = (pagination, filters, sorter, extra) => {
    // You can implement filter/sort logic here if needed
    console.log("params", pagination, filters, sorter, extra)
  }

  const filterTimeout = useRef(null)

  // Debounced filter function that combines all filter criteria
  const applyFilters = (fullName, companyType, companyName) => {
    if (filterTimeout.current) clearTimeout(filterTimeout.current)
    filterTimeout.current = setTimeout(() => {
      // Find company label from enum if companyType is selected
      let companyLabel = undefined
      if (companyType) {
        const selectedOption = packageCompanyType?.find(
          opt => opt.value === companyType
        )
        companyLabel = selectedOption ? selectedOption.label : undefined
      }

      props.onFilterPackageInstaller({
        fullName: fullName || undefined,
        packageCompanyTypeEnum: companyType || undefined,
        companyName: companyName || companyLabel || undefined,
      })
    }, 500)
  }

  // Filter handler for fullName
  const handleFullNameFilter = e => {
    const value = e.target.value
    setFilterFullName(value)
    applyFilters(value, filterPackageCompanyType, filterCompanyName)
  }

  // Filter handler for packageCompanyTypeEnum
  const handlePackageCompanyTypeFilter = value => {
    setFilterPackageCompanyType(value)
    applyFilters(filterFullName, value, filterCompanyName)
  }

  // Filter handler for companyName
  const handleCompanyNameFilter = e => {
    const value = e.target.value
    setFilterCompanyName(value)
    applyFilters(filterFullName, filterPackageCompanyType, value)
  }

  // Clear all filters
  const handleClearFilters = () => {
    setFilterFullName("")
    setFilterPackageCompanyType(undefined)
    setFilterCompanyName("")
    props.onFilterPackageInstaller({})
  }

  const handlePanelChange = key => {
    setActiveKey(key)
  }

  // Export filtered installers to Excel
  const handleExportToExcel = () => {
    const dataToExport =
      props.lstPackageInstallerFiltered &&
      props.lstPackageInstallerFiltered.length > 0
        ? props.lstPackageInstallerFiltered
        : props.lstPackageInstaller

    const exportData = dataToExport.map(x => ({
      "نام کامل": x.fullName || "",
      کدملی: x.naCode || "",
      "شماره موبایل": x.mobileNumber || "",
      "نام شرکت":
        (x.packageCompanyName !== "نامشخص"
          ? x.packageCompanyName
          : x.companyName) || "",
      شهر: x.cityName || "",
    }))
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "نصاب‌ها")
    XLSX.writeFile(workbook, "لیست نصاب‌ها.xlsx")
  }

  // Get grouped data based on filter state
  const getGroupedData = () => {
    if (
      props.lstPackageInstallerGroupedFiltered &&
      props.lstPackageInstallerGroupedFiltered.length > 0
    ) {
      return props.lstPackageInstallerGroupedFiltered
    }
    return props.lstPackageInstallerGrouped || []
  }

  const isExpiredLicense = value => {
    if (!value) return false
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return parsed < today
  }

  const getExpiredLicenseCount = record => {
    const children = record?.children || []
    return children.reduce((count, child) => {
      return isExpiredLicense(child?.julianLicenseExpire) ? count + 1 : count
    }, 0)
  }

  const getStatusCounts = record => {
    const children = record?.children || []
    return children.reduce(
      (acc, child) => {
        if (child?.isActive) acc.active += 1
        else acc.inactive += 1
        return acc
      },
      { active: 0, inactive: 0 }
    )
  }

  // Grouped table columns
  const groupedColumns = [
    {
      title: "نصاب",
      dataIndex: "fullName",
      key: "fullName",
      width: 250,
      render: (text, record) => (
        <div style={{ display: "flex", justifyContent: "right", gap: "12px" }}>
          <Avatar
            size={44}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            {text?.charAt(0) || "؟"}
          </Avatar>
          <div>
            <div
              style={{ fontWeight: "600", fontSize: "15px", color: "#1f2937" }}
            >
              {text || "بدون نام"}
            </div>
            <div style={{ color: "#6b7280", fontSize: "13px" }}>
              <IdcardOutlined style={{ marginLeft: "4px" }} />
              {record.naCode || "-"}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "موبایل",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 140,
      render: text => (
        <span
          style={{
            direction: "ltr",
            display: "inline-block",
            color: "#374151",
            fontFamily: "monospace",
          }}
        >
          <PhoneOutlined style={{ marginLeft: "6px", color: "#9ca3af" }} />
          {text || "-"}
        </span>
      ),
    },
    {
      title: "تعداد سوابق",
      dataIndex: "childCount",
      key: "childCount",
      width: 120,
      align: "center",
      render: count => (
        <Badge
          count={count}
          style={{
            backgroundColor: count > 1 ? "#667eea" : "#52c41a",
            fontWeight: "600",
            fontSize: "13px",
            padding: "0 10px",
          }}
          overflowCount={99}
        />
      ),
    },
    {
      title: "پروانه‌های منقضی",
      key: "expiredLicenseCount",
      width: 140,
      align: "center",
      render: (_, record) => {
        const expired = getExpiredLicenseCount(record)
        return (
          <Tooltip title="تعداد سوابق با پروانه منقضی">
            <Badge
              count={expired}
              showZero
              overflowCount={99}
              style={{
                backgroundColor: expired ? "#ff4d4f" : "#d9d9d9",
                color: expired ? "#fff" : "#595959",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
                minWidth: "36px",
              }}
            />
          </Tooltip>
        )
      },
    },
    {
      title: "وضعیت فعال/غیرفعال",
      key: "activeInactiveCount",
      width: 170,
      align: "center",
      render: (_, record) => {
        const { active, inactive } = getStatusCounts(record)
        return (
          <Space size={6}>
            <Tooltip title="فعال">
              <Badge
                count={active}
                showZero
                overflowCount={99}
                style={{
                  backgroundColor: "#52c41a",
                  boxShadow: "0 0 0 1px #d9f7be",
                  minWidth: "36px",
                }}
              />
            </Tooltip>
            <Tooltip title="غیرفعال">
              <Badge
                count={inactive}
                showZero
                overflowCount={99}
                style={{
                  backgroundColor: "#ff7875",
                  boxShadow: "0 0 0 1px #ffccc7",
                  minWidth: "36px",
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
    {
      title: "عملیات",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Tooltip title="برای مشاهده جزئیات کلیک کنید">
          <Button
            type="text"
            size="small"
            icon={
              expandedRowKeys.includes(record.key) ? (
                <DownOutlined />
              ) : (
                <RightOutlined />
              )
            }
            onClick={() => {
              if (expandedRowKeys.includes(record.key)) {
                setExpandedRowKeys(
                  expandedRowKeys.filter(k => k !== record.key)
                )
              } else {
                setExpandedRowKeys([...expandedRowKeys, record.key])
              }
            }}
            style={{
              color: "#667eea",
              transition: "transform 0.2s",
            }}
          />
        </Tooltip>
      ),
    },
  ]

  // Expanded row child columns
  const childColumns = [
    {
      title: "شرکت",
      dataIndex: "packageCompanyName",
      key: "packageCompanyName",
      align: "right",
      width: 150,
      render: (text, record) => (
        <Tag color="blue" style={{ fontSize: "12px" }}>
          <BankOutlined style={{ marginLeft: "4px" }} />
          {text !== "نامشخص" ? text : record.companyName || "نامشخص"}
        </Tag>
      ),
    },
    {
      title: "کد شرکت",
      align: "right",
      dataIndex: "companyCode",
      key: "companyCode",
      width: 100,
      render: text => text || "-",
    },
    {
      title: "شهر",
      dataIndex: "cityName",
      key: "cityName",
      align: "right",
      width: 120,
      render: text => (
        <span>
          <EnvironmentOutlined
            style={{ marginLeft: "4px", color: "#faad14" }}
          />
          {text || "-"}
        </span>
      ),
    },
    {
      title: "بخش‌ها",
      dataIndex: "sections",
      align: "right",
      key: "sections",
      width: 200,
      render: sections => {
        if (!sections || sections.length === 0) return "-"
        const first = sections[0]
        const rest = sections.slice(1)
        const allContent = (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              maxHeight: "260px",
              overflowY: "auto",
              minWidth: "140px",
            }}
          >
            {sections.map(sec => (
              <Tag key={sec.id} color="orange" style={{ margin: 0 }}>
                📍 {sec.sectionName || "-"}
              </Tag>
            ))}
          </div>
        )
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              flexWrap: "nowrap",
            }}
          >
            <Tag color="orange" style={{ margin: 0 }}>
              📍 {first.sectionName || "-"}
            </Tag>
            {rest.length > 0 && (
              <Popover
                content={allContent}
                title={`همه بخش‌ها (${sections.length})`}
                trigger="click"
                placement="bottomRight"
              >
                <Tag
                  color="geekblue"
                  style={{ cursor: "pointer", margin: 0, userSelect: "none" }}
                >
                  +{rest.length} بیشتر
                </Tag>
              </Popover>
            )}
          </div>
        )
      },
    },
    {
      title: "شماره پروانه",
      dataIndex: "licenseNumber",
      align: "center",
      key: "licenseNumber",
      width: 120,
      render: text => text || "-",
    },
    {
      title: "تاریخ انقضاء پروانه",
      dataIndex: "julianLicenseExpire",
      align: "center",
      key: "julianLicenseExpire",
      width: 120,
      render: date => {
        if (!date) return "-"
        const d = new Date(date)
        return d.toLocaleDateString("fa-IR")
      },
    },
    {
      title: "وضعیت",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      align: "center",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={checked => handleToggleActive(checked, record)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
          style={{
            backgroundColor: isActive ? "#52c41a" : "#ff4d4f",
          }}
        />
      ),
    },
    {
      title: "محدودیت شهر",
      dataIndex: "isLimitCity",
      key: "isLimitCity",
      width: 100,
      align: "center",
      render: (isLimitCity, record) => (
        <Switch
          checked={isLimitCity}
          onChange={checked => handleIsLimitCity(checked, record)}
          size="small"
        />
      ),
    },
    {
      title: "فایلها",
      key: "documents",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Tooltip title="مهر و امضاء">
          <div>
            <DocumentFilesDialog rowData={record} mainProps={props} />
          </div>
        </Tooltip>
      ),
    },
    {
      title: "عملیات",
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="ویرایش">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ borderRadius: "6px" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  // Expanded row render
  const expandedRowRender = record => {
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          borderRadius: "12px",
          padding: "16px",
          margin: "8px 0",
        }}
      >
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            justifyContent: "right",
            gap: "8px",
          }}
        >
          <TeamOutlined style={{ color: "#667eea", fontSize: "16px" }} />
          <Text strong style={{ color: "#374151" }}>
            اطلاعات {record.fullName} ({record.children?.length || 0} مورد)
          </Text>
        </div>
        <Table
          columns={childColumns}
          dataSource={record.children || []}
          pagination={false}
          size="small"
          rowKey="id"
          rowClassName={(_, i) => (i % 2 ? "child-row-odd" : "child-row-even")}
          style={{
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        />
      </div>
    )
  }

  return (
    <>
      {/* Header Section with Export Button */}
      <div
        style={{
          background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
          padding: "20px",
          borderRadius: "12px",
          marginBottom: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <h2
              style={{
                color: "white",
                margin: 0,
                fontSize: "24px",
                fontWeight: "600",
              }}
            >
              مدیریت نصاب‌ها
            </h2>
            <p
              style={{
                color: "rgba(255,255,255,0.8)",
                margin: "4px 0 0 0",
                fontSize: "14px",
              }}
            >
              ایجاد، ویرایش و مدیریت اطلاعات نصاب‌ها
            </p>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={handleOpenCreateModal}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "8px",
                  color: "white",
                  fontWeight: "500",
                }}
              >
                افزودن نصاب
              </Button>
              <Button
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleExportToExcel}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "8px",
                  color: "white",
                  fontWeight: "500",
                }}
              >
                خروجی اکسل
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Filter Section */}
      <Card
        style={{
          marginBottom: "16px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: "1px solid #e8e8e8",
        }}
        bodyStyle={{ padding: "16px 24px" }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "4px" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                <UserOutlined style={{ marginLeft: "4px" }} />
                جستجو بر اساس نام
              </Text>
            </div>
            <Input
              placeholder="نام نصاب را وارد کنید..."
              value={filterFullName}
              onChange={handleFullNameFilter}
              prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              size="large"
              style={{ borderRadius: "8px" }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "4px" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                <BankOutlined style={{ marginLeft: "4px" }} />
                نوع شرکت
              </Text>
            </div>
            <Select
              placeholder="انتخاب نوع شرکت"
              value={filterPackageCompanyType}
              onChange={handlePackageCompanyTypeFilter}
              options={packageCompanyType}
              allowClear
              size="large"
              style={{ width: "100%", borderRadius: "8px" }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "4px" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                <BankOutlined style={{ marginLeft: "4px" }} />
                نام شرکت
              </Text>
            </div>
            <Input
              placeholder="نام شرکت را وارد کنید..."
              value={filterCompanyName}
              onChange={handleCompanyNameFilter}
              prefix={<BankOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              size="large"
              style={{ borderRadius: "8px" }}
            />
          </Col>
          <Col xs={24} sm={12} md={24} lg={6}>
            <div
              style={{
                marginBottom: "4px",
                visibility: { xs: "visible", md: "hidden" },
              }}
            >
              <Text type="secondary" style={{ fontSize: "12px" }}>
                عملیات
              </Text>
            </div>
            <Space
              wrap
              style={{
                width: "100%",
                justifyContent: { xs: "flex-start", lg: "flex-end" },
              }}
            >
              <Button
                icon={<ReloadOutlined />}
                onClick={handleClearFilters}
                size="large"
                style={{ borderRadius: "8px" }}
              >
                پاک کردن
              </Button>
            </Space>
          </Col>
        </Row>
        {(filterFullName || filterPackageCompanyType || filterCompanyName) && (
          <Row style={{ marginTop: "12px" }}>
            <Col span={24}>
              <Space wrap>
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  فیلترهای فعال:
                </Text>
                {filterFullName && (
                  <Tag
                    color="blue"
                    closable
                    onClose={() => {
                      setFilterFullName("")
                      applyFilters(
                        "",
                        filterPackageCompanyType,
                        filterCompanyName
                      )
                    }}
                    style={{ borderRadius: "6px", padding: "4px 8px" }}
                  >
                    نام: {filterFullName}
                  </Tag>
                )}
                {filterPackageCompanyType && (
                  <Tag
                    color="green"
                    closable
                    onClose={() => {
                      setFilterPackageCompanyType(undefined)
                      applyFilters(filterFullName, undefined, filterCompanyName)
                    }}
                    style={{ borderRadius: "6px", padding: "4px 8px" }}
                  >
                    نوع:{" "}
                    {packageCompanyType?.find(
                      opt => opt.value === filterPackageCompanyType
                    )?.label || ""}
                  </Tag>
                )}
                {filterCompanyName && (
                  <Tag
                    color="purple"
                    closable
                    onClose={() => {
                      setFilterCompanyName("")
                      applyFilters(filterFullName, filterPackageCompanyType, "")
                    }}
                    style={{ borderRadius: "6px", padding: "4px 8px" }}
                  >
                    شرکت: {filterCompanyName}
                  </Tag>
                )}
              </Space>
            </Col>
          </Row>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <InstallerModal
        isModalOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        editingRecord={editingRecord}
        packageCompanyType={packageCompanyType}
      />

      <Table
        loading={props.loading}
        size="middle"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} از ${total} نصاب`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        scroll={{
          y: 600,
          x: 800,
        }}
        sticky
        childrenColumnName="antChildren"
        columns={groupedColumns}
        rowClassName={(record, i) => `grouped-row ${i % 2 ? "odd" : "even"}`}
        rowKey={record => record.key}
        dataSource={getGroupedData()}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record.key])
            } else {
              setExpandedRowKeys(expandedRowKeys.filter(k => k !== record.key))
            }
          },
          expandIcon: ({ expanded, onExpand, record }) => (
            <Button
              type="text"
              size="small"
              onClick={e => onExpand(record, e)}
              style={{
                transition: "transform 0.3s",
                transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                color: "#667eea",
              }}
            >
              <RightOutlined />
            </Button>
          ),
          rowExpandable: record =>
            record.children && record.children.length > 0,
        }}
        onChange={onChange}
        style={{
          borderRadius: "12px",
          overflow: "hidden",
        }}
      />

      {/* Custom styles for grouped table */}
      <style jsx="true">{`
        .grouped-row {
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .grouped-row:hover {
          background: linear-gradient(
            135deg,
            #f0f4ff 0%,
            #e8ecff 100%
          ) !important;
        }
        .grouped-row.odd {
          background-color: #fafbfc;
        }
        .grouped-row.even {
          background-color: #ffffff;
        }
        .child-row-odd {
          background-color: #f8fafc;
        }
        .child-row-even {
          background-color: #ffffff;
        }
        .ant-table-expanded-row > td {
          background: transparent !important;
          padding: 0 !important;
        }
        .ant-table-expanded-row-level-1 > td {
          border-bottom: none !important;
        }
      `}</style>
    </>
  )
}

const mapStateToProps = ({ PackageInstaller, USERs, Documents }) => ({
  lstPackageInstaller: PackageInstaller.lstPackageInstaller,
  lstPackageInstallerFiltered: PackageInstaller.lstPackageInstallerFiltered,
  lstPackageInstallerGrouped: PackageInstaller.lstPackageInstallerGrouped,
  lstPackageInstallerGroupedFiltered:
    PackageInstaller.lstPackageInstallerGroupedFiltered,
  error: PackageInstaller.error || USERs.error || Documents.error,
  success: PackageInstaller.success || USERs.success || Documents.success,
  loading: PackageInstaller.loading,
  lstUserFiles: USERs.lstUserFiles,
  lstDocuments: Documents.lstDocuments,
})

const mapDispatchToProps = dispatch => ({
  onUpsertPackageInstaller: data => dispatch(upsertPackageInstaller(data)),
  onGetListPackageInstaller: () => dispatch(getListPackageInstaller()),

  onFilterPackageInstaller: data => dispatch(filterPackageInstaller(data)),
  onFilterLoadingPackageInstaller: data =>
    dispatch(filterLoadingPackageInstaller(data)),
  onAddUserFile: attachData => dispatch(addUserFile(attachData)),
  onDeleteUserFile: id => dispatch(deleteUserFile(id)),
  onGetUserFiles: searchValue => dispatch(getUserFiles(searchValue)),
  onResetPackageInstallerFlag: () => dispatch(resetPackageInstallerFlag()),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(InstallerList))
